import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { metIntegration } from '@metservicedev/platforms-services-interface/src/services/metIntegration/metIntegration';
import { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import { usePersistedState } from '@mono/use-persisted-state';
import { usePlatStageContext } from '@mono/use-plat-stage/src/use-plat-stage';
import { RunwayConditionsSettings } from '@mono/types/src/runway-widgets';
import { IssueTime, InfoPopover, HoverButton } from '@mono/metui';
import { toHash } from '@mono/to-hash';
import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';

const HeaderCell = styled.div<{ mode?: 'cozy' | 'compact' }>`
    ${({ mode }) => TextStyles[mode ?? 'cozy'].h4}
    margin-right: 16px;
    width: ${({ mode }) => (mode === 'cozy' ? 64 : 50)}px;
    &:first-child {
        flex: 1;
    }

    &:last-child {
        text-align: right;
        margin-right: 8px;
    }
`;

const TableCell = styled.div<{ mode?: 'cozy' | 'compact' }>`
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p}
    margin-right: 16px;
    width: ${({ mode }) => (mode === 'cozy' ? 64 : 50)}px;
    &:first-child {
        flex: 1;
    }

    &:last-child {
        text-align: right;
        margin-right: 8px;
    }
`;

interface sensorOutput {
    status: string;
    depth: string;
    temp: string;
    condition: number;
}

interface sensorRow {
    0: sensorOutput;
    1: sensorOutput;
    2: sensorOutput;
}

const RunwaySign: React.FC<{ backgroundColor: string; mode?: 'cozy' | 'compact' }> = ({ children, backgroundColor, mode }) => {
    return (
        <div
            style={{
                width: 28,
                lineHeight: '27px',
                letterSpacing: '0.4px',
                fontWeight: 700,
                fontSize: mode === 'cozy' ? 20 : 12,
                backgroundColor,
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}
        >
            <div style={{ transform: 'rotate(90.1deg)', whiteSpace: 'nowrap' }}>{children}</div>
        </div>
    );
};

const getGradient = (colour: string) => {
    switch (colour) {
        case '#FA6161':
            return 'linear-gradient(122deg, #FA6161 0%, #E93F3F 100%)';
        case '#FF981B':
            return 'linear-gradient(121deg, #FF981B 0%, #CE7100 100%)';
        case '#74A30F':
        default:
            return 'linear-gradient(122deg, #74A30F 0%, #577E00 100%)';
    }
};

const RunwaySection = ({
    runwayCondition,
    thresholdColours,
}: {
    runwayCondition: sensorOutput | undefined;
    thresholdColours: [string, string, string, string, string, string];
}) => {
    const { condition, status } = runwayCondition || {};
    const text = status ?? 'n/a';
    const colour = condition && condition !== Number.MAX_SAFE_INTEGER ? getGradient(thresholdColours[condition - 1]) : '#909AB5';
    const code = condition && condition !== Number.MAX_SAFE_INTEGER ? condition : 'X';

    return (
        <div
            style={{
                flex: 1,
                borderRadius: 4,
                background: colour,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}
        >
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 68, fontWeight: 700, lineHeight: '92.6px', color: '#FFF' }}>{code}</div>
                <div style={{ fontSize: 16, fontWeight: 700, lineHeight: '22px', color: '#FFF' }}>{text}</div>
            </div>
        </div>
    );
};

const Dashboard = ({
    tableData,
    reverse,
    runways,
    thresholdColours,
    runwaySignColour,
    mode,
}: {
    tableData?: sensorRow[];
    reverse: boolean;
    runways: [number, number];
    thresholdColours: [string, string, string, string, string, string];
    runwaySignColour: string;
    mode?: 'compact' | 'cozy';
}) => {
    const runwayCondition = tableData?.reduce((lowest, currRow) => {
        return [
            currRow[0].condition < lowest[0].condition ? currRow[0] : lowest[0],
            currRow[1].condition < lowest[1].condition ? currRow[1] : lowest[1],
            currRow[2].condition < lowest[2].condition ? currRow[2] : lowest[2],
        ];
    });

    const runwaySections = [
        <RunwaySign key={'RunwaySign-0'} mode={mode} backgroundColor={runwaySignColour}>{`RUNWAY ${runways[0]}`}</RunwaySign>,
        <RunwaySection key={'RunwaySection-0'} runwayCondition={runwayCondition?.[0]} thresholdColours={thresholdColours} />,
        <RunwaySection key={'RunwaySection-1'} runwayCondition={runwayCondition?.[1]} thresholdColours={thresholdColours} />,
        <RunwaySection key={'RunwaySection-2'} runwayCondition={runwayCondition?.[2]} thresholdColours={thresholdColours} />,
        <RunwaySign key={'RunwaySign-1'} mode={mode} backgroundColor={runwaySignColour}>{`RUNWAY ${runways[1]}`}</RunwaySign>,
    ];

    const sections = reverse ? runwaySections.reverse() : runwaySections;

    return <div style={{ display: 'flex', columnGap: 4, flex: 1, marginTop: 10 }}>{sections}</div>;
};

const TableHeader = ({ mode }: { mode?: 'cozy' | 'compact' }) => {
    return (
        <div style={{ display: 'flex', borderBottom: '1px solid #CDCDCD', columnGap: 4 }}>
            {[0, 1, 2].map((i) => {
                return (
                    <div key={`table-header-${i}`} style={{ display: 'flex', flex: 1, padding: '12px 0' }}>
                        <HeaderCell mode={mode}>STATUS</HeaderCell>
                        <HeaderCell mode={mode}>DEPTH</HeaderCell>
                        <HeaderCell mode={mode}>TEMP</HeaderCell>
                    </div>
                );
            })}
        </div>
    );
};

const TableSensor = ({ output, mode }: { output: sensorOutput; mode?: 'cozy' | 'compact' }) => {
    const { depth, status, temp } = output;
    return (
        <div style={{ display: 'flex', flex: 1 }}>
            <TableCell mode={mode}>{status}</TableCell>
            <TableCell mode={mode}>{depth}</TableCell>
            <TableCell mode={mode}>{temp}</TableCell>
        </div>
    );
};

const Table = ({ tableData, reverse, mode }: { tableData?: sensorRow[]; reverse: boolean; mode?: 'cozy' | 'compact' }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <TableHeader mode={mode} />
            {tableData?.map((sensor, index) => {
                return (
                    <div
                        key={`Table-${index}-row`}
                        style={{
                            display: 'flex',
                            columnGap: 4,
                            ...(index === tableData.length - 1 ? { paddingTop: 12 } : { borderBottom: '1px solid #CDCDCD', padding: '12px 0' }),
                        }}
                    >
                        <TableSensor mode={mode} output={sensor[reverse ? 2 : 0]} />
                        <TableSensor mode={mode} output={sensor[1]} />
                        <TableSensor mode={mode} output={sensor[reverse ? 0 : 2]} />
                    </div>
                );
            })}
        </div>
    );
};

const RunwayConditionVisualisation = ({
    tableData,
    reverse,
    runways,
    thresholdColours,
    theme,
    mode = 'cozy',
}: {
    tableData?: sensorRow[];
    reverse: boolean;
    runways: [number, number];
    thresholdColours: [string, string, string, string, string, string];
    theme: { text: string; sign: string };
    mode?: 'compact' | 'cozy';
}) => {
    return (
        <>
            <Dashboard
                tableData={tableData}
                reverse={reverse}
                runways={runways}
                thresholdColours={thresholdColours}
                runwaySignColour={theme.sign}
                mode={mode}
            />
            <div style={{ display: 'flex', paddingTop: 6 }}>
                <div style={{ width: 30 }} />
                <Table tableData={tableData} reverse={reverse} mode={mode} />
                <div style={{ width: 30 }} />
            </div>
        </>
    );
};

const sentenceCase = (str: string) => {
    if (!str || str.length === 0) return 'X';
    return str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

interface aviationAPIRespsonse {
    'av:airport-location': string;
    'av:data-used-in-talpa': string; // string of "true" or "false"
    'av:runway-sensor-id': string;
    'met:domain': string;
    'met:issue-time': string;
    'met:product-name': 'runwaysensor';
    'met:valid-at': string;
    'met:data': {
        observationTimestamp: string; //timestamp in weird format
        roadConditionText01MinAvg: string; // DRY
        roadConditionValue01MinAvg: string; // "6"
        roadFilm01MinAvg: string; //"0.00"
        roadFilm10MinAvg: string; //"0.00"
        roadFriction01MinAvg: string; //""
        roadTemp01MinAvg: string; //"33.7"
        roadTemp10MinAvg: string; //"34.3"
        stationId: string; //"NZWNF"
        stationNumericId: string; //"93963"
    };
}

interface runwayResponse {
    sensorData: {
        sensor: string;
        data?: aviationAPIRespsonse;
        error?: string;
    }[];
}

export const RunwayConditions = ({ settings, theme: themeName }: WeatherWidgetRenderProps<RunwayConditionsSettings>) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const { stage } = usePlatStageContext();

    const { id, title, aerodrome, runways, timezone, sensors, thresholdColours } = settings;
    const [sensorData, setSensorData] = useState<runwayResponse['sensorData']>();

    const [reverse, setReverse] = usePersistedState({
        initialState: false,
        key: `mov-runway-conditions-${id}-reverse`,
    });

    const targetTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    const isDarkMode = themeName === 'dark';
    const theme = isDarkMode
        ? {
              text: '#ffffff',
              sign: '#F7F7F71A',
              issueTime: '#ffffff',
          }
        : {
              text: '#172344',
              sign: '#F7F7F7',
              issueTime: '#595959',
          };

    const toolbarStyle = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    } as React.CSSProperties;

    useEffect(() => {
        const metjwt = localStorage.getItem('metjwt');
        const { metserviceApi } = metIntegration;

        let timeoutHook = 0;
        const patientQuery = () => {
            const headers: {} | { authorization: string } = metjwt
                ? {
                      authorization: 'Bearer ' + metjwt,
                  }
                : {};

            try {
                Promise.all(
                    sensors.flat().map((sensor) => {
                        return fetch(`${metserviceApi[stage]}/aviation/observations/airport/${aerodrome}/sensor/${sensor}/latest`, { headers })
                            .then((res) => {
                                return res.json().then((data) => {
                                    return { sensor, data: data.response[0] };
                                });
                            })
                            .catch((err) => {
                                console.log(err);
                                return { sensor, data: undefined, error: err };
                            });
                    })
                ).then((response) => {
                    setSensorData(response);
                });
            } catch (e) {
                console.error(e);
            } finally {
                timeoutHook = setTimeout(patientQuery, 60 * 1000) as unknown as number;
            }
        };

        patientQuery();

        return () => {
            clearTimeout(timeoutHook);
        };
    }, []);

    const tableData: sensorRow[] | undefined = useMemo(() => {
        if (!sensorData || sensorData.length !== sensors.flat().length) return;
        const mappedSensordata = sensors.map((sensorRow) => sensorRow.map((sensorName) => sensorData.find(({ sensor }) => sensor === sensorName)?.data));
        const orientedSensordata = reverse ? [...mappedSensordata].reverse() : mappedSensordata;
        const tableData = orientedSensordata.map((sensorRow) => {
            return {
                0: {
                    status:
                        sensorRow[0] && sensorRow[0]['met:data']['roadConditionText01MinAvg']
                            ? sentenceCase(sensorRow[0]['met:data']['roadConditionText01MinAvg'])
                            : 'n/a',
                    depth: sensorRow[0] && sensorRow[0]['met:data']['roadFilm01MinAvg'] ? sensorRow[0]['met:data']['roadFilm01MinAvg'] : 'n/a',
                    temp: sensorRow[0] && sensorRow[0]['met:data']['roadTemp01MinAvg'] ? sensorRow[0]['met:data']['roadTemp01MinAvg'] + '°' : 'n/a',
                    condition:
                        sensorRow[0] && sensorRow[0]['met:data']['roadConditionValue01MinAvg']
                            ? parseInt(sensorRow[0]['met:data']['roadConditionValue01MinAvg'])
                            : Number.MAX_SAFE_INTEGER,
                },
                1: {
                    status:
                        sensorRow[1] && sensorRow[1]['met:data']['roadConditionText01MinAvg']
                            ? sentenceCase(sensorRow[1]['met:data']['roadConditionText01MinAvg'])
                            : 'n/a',
                    depth: sensorRow[1] && sensorRow[1]['met:data']['roadFilm01MinAvg'] ? sensorRow[1]['met:data']['roadFilm01MinAvg'] : 'n/a',
                    temp: sensorRow[1] && sensorRow[1]['met:data']['roadTemp01MinAvg'] ? sensorRow[1]['met:data']['roadTemp01MinAvg'] + '°' : 'n/a',
                    condition:
                        sensorRow[1] && sensorRow[1]['met:data']['roadConditionValue01MinAvg']
                            ? parseInt(sensorRow[1]['met:data']['roadConditionValue01MinAvg'])
                            : Number.MAX_SAFE_INTEGER,
                },
                2: {
                    status:
                        sensorRow[2] && sensorRow[2]['met:data']['roadConditionText01MinAvg']
                            ? sentenceCase(sensorRow[2]['met:data']['roadConditionText01MinAvg'])
                            : 'n/a',
                    depth: sensorRow[2] && sensorRow[2]['met:data']['roadFilm01MinAvg'] ? sensorRow[2]['met:data']['roadFilm01MinAvg'] : 'n/a',
                    temp: sensorRow[2] && sensorRow[2]['met:data']['roadTemp01MinAvg'] ? sensorRow[2]['met:data']['roadTemp01MinAvg'] + '°' : 'n/a',
                    condition:
                        sensorRow[2] && sensorRow[2]['met:data']['roadConditionValue01MinAvg']
                            ? parseInt(sensorRow[2]['met:data']['roadConditionValue01MinAvg'])
                            : Number.MAX_SAFE_INTEGER,
                },
            };
        });

        return tableData;
    }, [toHash(sensorData), reverse, toHash(sensors)]);

    const issueTime = sensorData?.[0].data?.['met:issue-time'] ?? '';

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                minWidth: 400,
                minHeight: 200,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                font: '400 12px/18px Open Sans',
                color: theme.text,
            }}
            ref={chartRef}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={toolbarStyle}>
                    <div style={{ font: '700 16px/22px Open Sans' }}>{title}</div>
                </div>
                <div style={{ display: 'flex', columnGap: 4 }}>
                    <IssueTime issueTime={issueTime} targetTimezone={targetTimezone} style={toolbarStyle} colour={theme.issueTime} isDarkMode={isDarkMode} />
                    <div style={toolbarStyle}>
                        <HoverButton onClick={() => setReverse(!reverse)}>
                            <span style={{ display: 'flex', justifyContent: 'center' }}>
                                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                    <g clipPath='url(#clip0_1139_3490)'>
                                        <path d='M9.01 14H2V16H9.01V19L13 15L9.01 11V14ZM14.99 13V10H22V8H14.99V5L11 9L14.99 13Z' fill='#595959' />
                                    </g>
                                    <defs>
                                        <clipPath id='clip0_1139_3490'>
                                            <rect width='24' height='24' fill='white' />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>
                        </HoverButton>
                    </div>
                    <div style={toolbarStyle}>
                        <InfoPopover>
                            <div style={{ padding: '5px 16px' }}>
                                <div style={{ display: 'flex' }}>
                                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M12 21C8.68629 21 6 18.3137 6 15C6 12.7909 8 8.79086 12 3C16 8.79086 18 12.7909 18 15C18 18.3137 15.3137 21 12 21ZM15.6293 12C14.8901 10.3499 13.6804 8.23023 12 5.68003C10.3196 8.23023 9.10988 10.3499 8.37074 12H15.6293Z'
                                            fill='#909AB5'
                                        />
                                    </svg>
                                    <div style={{ paddingLeft: 8, paddingBottom: 8, font: '400 12px/18px Open Sans' }}>
                                        <div style={{ color: '#595959', fontWeight: 700, fontSize: 14 }}>RUNWAY SURFACE CONDITIONS</div>
                                        <span style={{ color: thresholdColours[5], fontWeight: 700 }}>6 (Dry)</span>: Water depth 0.00mm
                                        <br />
                                        <span style={{ color: thresholdColours[4], fontWeight: 700 }}>5 (Wet)</span>: Water depth between 0.04mm and 3.00mm
                                        <br />
                                        <span style={{ color: thresholdColours[4], fontWeight: 700 }}>5 (Frost)</span>: Condition reported directly from sensor
                                        <br />
                                        <span style={{ color: thresholdColours[3], fontWeight: 700 }}>4 (Compacted Snow)</span>: Condition not reported by
                                        sensor
                                        <br />
                                        <span style={{ color: thresholdColours[2], fontWeight: 700 }}>3 (Wet Snow)</span>: Condition reported directly from
                                        sensor
                                        <br />
                                        <span style={{ color: thresholdColours[1], fontWeight: 700 }}>2 (Standing Water)</span>: Water depth greater than 3.00mm
                                        <br />
                                        <span style={{ color: thresholdColours[0], fontWeight: 700 }}>1 (Ice)</span>: Condition reported directly from sensor
                                        <br />
                                        <span style={{ color: '#595959', fontWeight: 700 }}>X (No Data)</span>: All sensors in this zone are not working
                                    </div>
                                </div>
                                <div style={{ color: '#595959' }}>
                                    Observations are taken from {sensorData?.length ?? 0} sensors embedded in the runway surface, {sensors?.length ?? 0} are
                                    positioned in each runway third.
                                    <br />
                                    Data is expected every 1-2 minutes
                                </div>
                                <div></div>
                            </div>
                        </InfoPopover>
                    </div>
                </div>
            </div>
            <hr style={{ width: '100%', border: 'none', borderBottom: '1px solid #cdcdcd' }} />
            <RunwayConditionVisualisation
                tableData={tableData}
                reverse={reverse}
                runways={runways}
                thresholdColours={thresholdColours}
                theme={theme}
                mode={settings.mode}
            />
        </div>
    );
};
